/**
 * Custom Plywood Dimensions Popup
 * Shows a popup when custom plywood items in cart are missing dimensions
 *
 * SUMMARY:
 * This JavaScript file implements a Custom Plywood Dimensions Popup system for a Shopify store.
 *
 * Main Purpose:
 * - Detects when customers have custom plywood items in their cart that are missing required dimensions (length and width)
 * - Shows a popup to help customers either reuse previous custom orders or create new ones
 *
 * Key Features:
 * 1. Cart Monitoring - Listens for cart changes and additions, automatically checks for custom plywood items missing dimensions on page load
 * 2. Custom Plywood Detection - Identifies custom plywood products by title ("Custom Cut Plywood") or setup fee properties, checks if required dimensions ("Length in Inches" and "Width in Inches") are missing
 * 3. Previous Orders Integration - Fetches customer's previous custom plywood orders from an API, displays them in a user-friendly format with product details, dimensions, and pricing
 * 4. Interactive Popup Interface - Shows a modal with previous orders, includes quantity controls (+/- buttons) for each order, "Add to Cart" buttons to quickly reorder previous configurations, links to create new custom orders or login for account access
 * 5. Smart Cart Management - Removes incomplete custom plywood items (without dimensions) before adding new ones, handles associated setup fees automatically, updates cart and triggers necessary events
 * 6. Configurator Logic Integration - Uses the same logic patterns as the custom product configurator for perfect synchronization
 * 7. Smart Setup Fee Handling - Checks if setup fee is already covered by existing cart items, only shows setup fee when needed
 * 8. Dynamic Pricing Display - Shows unit price and total price (unit × quantity), with setup fee charged only once per configuration
 *
 * Technical Implementation:
 * - Uses modern ES6+ class syntax (Shopify-compatible)
 * - Integrates with Shopify's cart API (/cart.js, /cart/add.js, /cart/change.js)
 * - Implements proper event handling and DOM manipulation
 * - Includes responsive design with inline styling
 * - Contains all utility functions within the popup file for better organization
 * - Maintains perfect sync with custom product configurator behavior
 * - Handles new JSON structure with separate setup fee items
 *
 * This system provides a seamless way for customers to avoid having incomplete custom plywood orders in their cart by offering quick access to their previous configurations, while maintaining perfect synchronization with the main configurator and smart setup fee management.
 */

// All utility functions are kept within this popup file for better organization

class CustomPlywoodPopup {
  /**
   * Calculate pricing using configurator logic
   * @param {Object} customPricing - The custom_pricing object
   * @param {string} lengthValue - Length dimension value
   * @param {string} widthValue - Width dimension value
   * @returns {number} Calculated total price
   */
  calculatePricing(customPricing, lengthValue, widthValue) {
    if (!customPricing || !customPricing.pricingUpcharges) {
      console.warn("No custom pricing found");
      return 0;
    }

    // Get pricing entries using configurator logic
    const entries = Object.entries(customPricing.pricingUpcharges);

    // Find length and width entries (same as configurator's activeLengthEntry/activeWidthEntry)
    const lengthEntry = entries.find(e => e[0] === lengthValue);
    const widthEntry = entries.find(e => e[0] === widthValue);

    // Extract prices using configurator logic: activeLengthEntryPrice + activeWidthEntryPrice
    const activeLengthEntryPrice = lengthEntry && lengthEntry[1] ? lengthEntry[1] : 0;
    const activeWidthEntryPrice = widthEntry && widthEntry[1] ? widthEntry[1] : 0;

    // Calculate total price (same as configurator's totalProductPrice)
    return activeLengthEntryPrice + activeWidthEntryPrice;
  }

  /**
   * Build selection unique key using configurator logic
   * @param {string} variantId - The variant ID
   * @param {string} lengthValue - Length dimension value
   * @param {string} widthValue - Width dimension value
   * @returns {string} Unique key in format: variantId-lengthValue-widthValue
   */
  buildSelectionUniqueKey(variantId, lengthValue, widthValue) {
    return `${variantId}-${lengthValue}-${widthValue}`;
  }

  /**
   * Build selection title using configurator logic
   * @param {string} variantTitle - The variant title
   * @param {string} lengthValue - Length dimension value
   * @param {string} widthValue - Width dimension value
   * @param {string} productTitle - The product title
   * @returns {string} Formatted selection title
   */
  buildSelectionTitle(variantTitle, lengthValue, widthValue, productTitle) {
    return `${variantTitle} x ${widthValue}" x ${lengthValue}" ${productTitle}`;
  }

  /**
   * Check if setup fee should be added using configurator logic
   * @param {Array} cartItems - Current cart items
   * @param {string} selectionUniqueKey - The selection unique key
   * @returns {boolean} Whether setup fee should be added
   */
  shouldAddSetupFee(cartItems, selectionUniqueKey) {
    const cartHasSelectedSetupFee = cartItems.some(
      item =>
        item.product_type === "Setup Fee" &&
        (item.properties._selectionUniqueKey === selectionUniqueKey ||
          item.properties._setupFeeFor === selectionUniqueKey)
    );
    return !cartHasSelectedSetupFee;
  }

  /**
   * Build properties object using configurator logic
   * @param {string} variantId - The variant ID
   * @param {string} lengthValue - Length dimension value
   * @param {string} widthValue - Width dimension value
   * @param {number} calculatedPrice - The calculated price
   * @param {Object} existingProperties - Existing properties from order data
   * @param {string} setupFeeItemId - Setup fee product ID
   * @returns {Object} Properties object matching configurator format
   */
  buildProperties(
    variantId,
    lengthValue,
    widthValue,
    calculatedPrice,
    existingProperties,
    setupFeeItemId
  ) {
    const productTitle = existingProperties._selectionTitle || "Custom Cut Plywood";
    const variantTitle = existingProperties._variantTitle || "Custom Cut Plywood";

    return {
      _selectionTitle: this.buildSelectionTitle(
        variantTitle,
        lengthValue,
        widthValue,
        productTitle
      ),
      _selectionUniqueKey: this.buildSelectionUniqueKey(variantId, lengthValue, widthValue),
      _selectionPrice: calculatedPrice.toFixed(2),
      _setupFeeItemId: setupFeeItemId || "",
      "Length in Inches": lengthValue,
      "Width in Inches": widthValue,
      // Preserve any other existing properties
      ...existingProperties,
    };
  }
  constructor() {
    this.init();
  }

  init() {
    // Listen for cart changes
    document.addEventListener("on:cart:change", () => this.checkCustomPlywoodDimensions());
    document.addEventListener("on:cart:add", () => this.checkCustomPlywoodDimensions());

    // Also check on page load
    document.addEventListener("DOMContentLoaded", () => this.checkCustomPlywoodDimensions());
  }

  async checkCustomPlywoodDimensions() {
    try {
      const cartItems = await this.getCartItems();

      // Check if there are any custom plywood items without dimensions
      const customPlywoodWithoutDimensions = cartItems.filter(item => {
        // Check if it's a custom plywood product (by title or setup fee property)
        const isCustomPlywood =
          item.title.includes("Custom Cut Plywood") ||
          item.product_title === "Custom Cut Plywood" ||
          item.properties._setupFeeItemId;

        if (!isCustomPlywood) return false;

        // Check if dimensions are missing (cart items have properties as flat object)
        const hasLength =
          item.properties["Length in Inches"] &&
          String(item.properties["Length in Inches"]).trim() !== "";
        const hasWidth =
          item.properties["Width in Inches"] &&
          String(item.properties["Width in Inches"]).trim() !== "";

        return !hasLength || !hasWidth;
      });

      // If we found custom plywood without dimensions, show the popup
      if (customPlywoodWithoutDimensions.length > 0) {
        this.showCustomPlywoodDimensionsPopup();
      }

      // Force popup if testing toggle is enabled
      if (
        this.forcePopup &&
        cartItems.some(
          item =>
            item.title.includes("Custom Cut Plywood") ||
            item.product_title === "Custom Cut Plywood" ||
            item.properties._setupFeeItemId
        )
      ) {
        console.log("TESTING: Force popup enabled - showing popup");
        this.showCustomPlywoodDimensionsPopup();
      }
    } catch (error) {
      console.error("Error checking custom plywood dimensions:", error);
    }
  }

  async getCartItems() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      return data.items;
    } catch (error) {
      console.error("Failed to get cart items:", error);
      return [];
    }
  }

  async showCustomPlywoodDimensionsPopup() {
    // Check if popup already exists to avoid duplicates
    if (document.getElementById("custom-plywood-dimensions-popup")) {
      return;
    }

    // Fetch previous orders
    const previousOrders = await this.fetchPreviousCustomPlywoodOrders();

    // Create the popup modal
    const popup = document.createElement("modal-dialog");
    popup.className = "modal fixed top-0 left-0 w-full h-full flex items-center justify-center";
    popup.id = "custom-plywood-dimensions-popup";
    popup.setAttribute("role", "dialog");
    popup.setAttribute("aria-modal", "true");
    popup.setAttribute("tabindex", "-1");

    popup.innerHTML = `
      <div class="modal__window relative bg-theme-bg text-theme-text text-start overflow-hidden has-motion" style="max-width: 600px; width: 90%;">
        <button type="button" class="modal__close-btn absolute js-close-modal">
          <span class="visually-hidden">Close</span>
          <svg class="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="modal__content flex-auto h-full rte p-6">
          <div class="h3 mb-4" style="color: #1e40af; font-weight: bold; font-family: 'Brown Std', sans-serif !important;">Oops! Your custom plywood order needs dimensions.</div>
          <p class="mb-4" style="color: #1e40af; font-family: 'Brown Std', sans-serif;">
            Choose from your previous custom orders below or click 
            <a href="/products/custom-plywood" class="create-new-order-link" style="color: #1e40af; text-decoration: underline; font-family: 'Brown Std', sans-serif;">here</a> 
            to create a new custom order.
          </p>
          
          <div class="previous-orders-section mb-4">
            ${await this.renderPreviousOrders(previousOrders)}
          </div>
          
          <div class="login-prompt" style="color: #1e40af; font-family: 'Brown Std', sans-serif;">
            Don't see your previous order here? 
            <a href="/account/login" style="color: #1e40af; text-decoration: underline; font-family: 'Brown Std', sans-serif;">Login</a> 
            to access your order info.
          </div>
          
          <div class="modal-actions" style="margin-top: 20px; display: flex; gap: 12px; justify-content: center;">
            <button type="button" class="back-to-cart-btn" style="background: #6b7280; color: white; border: none; border-radius: 4px; padding: 8px 16px; font-size: 14px; cursor: pointer; font-family: 'Brown Std', sans-serif;">
              Back to Cart
            </button>
          </div>
        </div>
      </div>
    `;

    // Add to page and open
    document.body.appendChild(popup);
    popup.open();

    // Add event listeners for quantity controls
    this.addQuantityEventListeners(popup);
  }

  async fetchPreviousCustomPlywoodOrders() {
    try {
      // Check if we should use API data (default: true)
      const useApiData = this.useApiData !== false;

      if (!useApiData) {
        console.log("Using mock data instead of API");
        return this.generateMockOrders();
      }

      // Determine if we're in development or production
      const isDevelopment =
        window.location.hostname.includes("localhost") ||
        window.location.hostname.includes("127.0.0.1");

      let apiUrl;
      if (isDevelopment) {
        apiUrl =
          "https://se-extensions.gadget.app/woodpeckers-crafts-api?logged_in_customer_id=8576693797043&path_prefix=/a/woodpeckers-crafts-api&shop=woodpeckerscrafts.myshopify.com";
      } else {
        apiUrl = "/a/woodpeckers-crafts-api";
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const apiOrders = data.plywoodOrders || [];

      console.log("API orders found:", apiOrders.length);
      return apiOrders;
    } catch (error) {
      console.error("Error fetching previous custom plywood orders:", error);
      // Return empty array if API fails (no fallback to mock data in production)
      return [];
    }
  }

  /**
   * Generate mock orders for testing multiple order scenarios
   * @returns {Array} Array of mock order objects
   */
  generateMockOrders() {
    return [
      {
        orderId: "6220914065588",
        orderName: "S203184",
        orderDate: "2025-09-09T10:30:00.000Z",
        items: [
          {
            itemName: "One-Time Setup Fee",
            price: "9.99",
            quantity: 1,
            properties: [
              {
                name: "_selectionUniqueKey",
                value: "44787441926323-8-12",
              },
            ],
            customPricing: false,
            setupFeeProduct: "",
          },
          {
            itemName: "Custom Cut Plywood",
            price: "12.3",
            quantity: 2,
            properties: [
              {
                name: "_selectionTitle",
                value: '1/4 Inch x 8" x 12" Custom Cut Plywood',
              },
              {
                name: "_selectionUniqueKey",
                value: "44787441926323-8-12",
              },
              {
                name: "_selectionPrice",
                value: 12.3,
              },
              {
                name: "_setupFeeItemId",
                value: 44843951063219,
              },
              {
                name: "Length in Inches",
                value: 12,
              },
              {
                name: "Width in Inches",
                value: 8,
              },
            ],
            customPricing: {
              pricingUpcharges: {
                6: 1.1,
                7: 1.5,
                8: 1.9,
                9: 2.4,
                10: 3,
                11: 3.6,
                12: 4.3,
                13: 5,
                14: 5.8,
                15: 6.7,
                16: 7.6,
                17: 8.6,
                18: 9.6,
                19: 10.7,
                20: 11.9,
                21: 13.1,
                22: 14.4,
                23: 15.7,
                24: 17.1,
                25: 18.5,
                26: 20,
                27: 21.6,
                28: 23.2,
                29: 24.9,
                30: 26.7,
                31: 28.5,
                32: 30.3,
                33: 32.3,
                34: 34.2,
                35: 36.3,
                36: 38.4,
                37: 40.5,
                38: 42.8,
                39: 45,
                40: 47.4,
                41: 49.8,
                42: 52.2,
                43: 54.7,
                44: 57.3,
                45: 60,
                46: 62.6,
                47: 65.4,
                48: 68.2,
              },
            },
            setupFeeProduct: "gid://shopify/Product/8299569348787",
          },
        ],
      },
      {
        orderId: "6220914065589",
        orderName: "S203185",
        orderDate: "2025-09-08T14:15:00.000Z",
        items: [
          {
            itemName: "One-Time Setup Fee",
            price: "9.99",
            quantity: 1,
            properties: [
              {
                name: "_selectionUniqueKey",
                value: "44787442909363-10-20",
              },
            ],
            customPricing: false,
            setupFeeProduct: "",
          },
          {
            itemName: "Custom Cut Plywood",
            price: "15.8",
            quantity: 1,
            properties: [
              {
                name: "_selectionTitle",
                value: '1/2 Inch x 10" x 20" Custom Cut Plywood',
              },
              {
                name: "_selectionUniqueKey",
                value: "44787442909363-10-20",
              },
              {
                name: "_selectionPrice",
                value: 15.8,
              },
              {
                name: "_setupFeeItemId",
                value: 44843951063219,
              },
              {
                name: "Length in Inches",
                value: 20,
              },
              {
                name: "Width in Inches",
                value: 10,
              },
            ],
            customPricing: {
              pricingUpcharges: {
                6: 1.1,
                7: 1.5,
                8: 1.9,
                9: 2.4,
                10: 3,
                11: 3.6,
                12: 4.3,
                13: 5,
                14: 5.8,
                15: 6.7,
                16: 7.6,
                17: 8.6,
                18: 9.6,
                19: 10.7,
                20: 11.9,
                21: 13.1,
                22: 14.4,
                23: 15.7,
                24: 17.1,
                25: 18.5,
                26: 20,
                27: 21.6,
                28: 23.2,
                29: 24.9,
                30: 26.7,
                31: 28.5,
                32: 30.3,
                33: 32.3,
                34: 34.2,
                35: 36.3,
                36: 38.4,
                37: 40.5,
                38: 42.8,
                39: 45,
                40: 47.4,
                41: 49.8,
                42: 52.2,
                43: 54.7,
                44: 57.3,
                45: 60,
                46: 62.6,
                47: 65.4,
                48: 68.2,
              },
            },
            setupFeeProduct: "gid://shopify/Product/8299569348787",
          },
        ],
      },
    ];
  }

  /**
   * Render previous orders with smart setup fee handling
   * Processes orders to match plywood items with their setup fees and checks cart state
   * @param {Array} orders - Array of order objects from API
   * @returns {string} HTML string for previous orders display
   */
  async renderPreviousOrders(orders) {
    if (!orders || orders.length === 0) {
      return '<p style="color: #1e40af;">No previous custom plywood orders found.</p>';
    }

    // Process orders to extract plywood items with their associated setup fees
    const plywoodItemsWithSetupFees = [];

    orders.forEach(order => {
      // Find plywood items in this order
      const plywoodItems = order.items.filter(
        item =>
          item.itemName === "Custom Cut Plywood" &&
          item.properties.some(prop => prop.name === "Length in Inches")
      );

      // Find setup fee items in this order
      const setupFeeItems = order.items.filter(item => item.itemName === "One-Time Setup Fee");

      // Match plywood items with their setup fees
      plywoodItems.forEach(plywoodItem => {
        const getProperty = name => {
          const prop = plywoodItem.properties.find(p => p.name === name);
          return prop ? prop.value : null;
        };

        const selectionKey = getProperty("_selectionUniqueKey");

        // Find matching setup fee for this plywood item
        const matchingSetupFee = setupFeeItems.find(setupFee => {
          const setupFeeSelectionKey = setupFee.properties.find(
            p => p.name === "_selectionUniqueKey"
          )?.value;
          return setupFeeSelectionKey === selectionKey;
        });

        plywoodItemsWithSetupFees.push({
          ...plywoodItem,
          orderId: order.orderId,
          orderName: order.orderName,
          orderDate: order.orderDate,
          matchingSetupFee: matchingSetupFee || null,
        });
      });
    });

    if (plywoodItemsWithSetupFees.length === 0) {
      return '<p style="color: #1e40af;">No previous custom plywood orders found.</p>';
    }

    // Calculate pricing using configurator logic for each item (no API calls needed - uses order data)
    const itemsWithPricing = await Promise.all(
      plywoodItemsWithSetupFees.map(async order => {
        const getProperty = name => {
          const prop = order.properties.find(p => p.name === name);
          return prop ? prop.value : null;
        };

        const selectionTitle = getProperty("_selectionTitle");
        const thickness = this.extractThicknessFromTitle(selectionTitle);
        const length = getProperty("Length in Inches");
        const width = getProperty("Width in Inches");
        const selectionKey = getProperty("_selectionUniqueKey");
        const variantId = selectionKey ? selectionKey.split("-")[0] : null;
        const originalPrice = parseFloat(getProperty("_selectionPrice")) || 0;

        // Calculate pricing using configurator logic (uses custom_pricing from order data)
        let calculatedPrice = 0;
        if (length && width && order.custom_pricing) {
          calculatedPrice = this.calculatePricing(order.custom_pricing, length, width);
          console.log(
            `Calculated price for ${length}" x ${width}": $${calculatedPrice.toFixed(2)}`
          );
        } else {
          console.log("Missing data for pricing calculation:", {
            length,
            width,
            customPricing: order.custom_pricing,
          });
        }

        // Get setup fee price from the matching setup fee item
        const setupFeePrice = order.matchingSetupFee
          ? parseFloat(order.matchingSetupFee.price)
          : null;

        // Check if this selection already exists in the cart (setup fee already covered)
        const cartItems = await this.getCartItems();
        const currentSelectionKey = getProperty("_selectionUniqueKey");
        const setupFeeNeeded =
          setupFeePrice !== null && this.shouldAddSetupFee(cartItems, currentSelectionKey);

        return {
          ...order,
          thickness,
          length,
          width,
          calculatedPrice,
          originalPrice,
          setupFeeNeeded,
          setupFeePrice,
        };
      })
    );

    return `
      <div class="previous-orders-list">
        ${itemsWithPricing
          .map(item => {
            return `
          <div class="order-item" style="display: flex; align-items: center; padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
            <div class="product-image" style="width: 60px; height: 60px; margin-right: 12px; background: #d2b48c; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #8b4513;">
              CUSTOM CUT PLYWOOD
            </div>
            <div class="product-details" style="flex: 1; color: #1e40af; font-family: 'Brown Std', sans-serif;">
              <div style="font-weight: bold;">Custom Cut Plywood</div>
              <div>Thickness: ${item.thickness || "N/A"}</div>
              <div>Length in Inches: ${item.length || "N/A"}</div>
              <div>Width in Inches: ${item.width || "N/A"}</div>
            </div>
            <div class="quantity-controls" style="margin-right: 12px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <button type="button" class="quantity-decrease" style="background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 4px; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;">-</button>
                <span class="quantity-display" style="min-width: 20px; text-align: center; font-family: 'Brown Std', sans-serif;">${
                  item.quantity || 1
                }</span>
                <button type="button" class="quantity-increase" style="background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 4px; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;">+</button>
              </div>
              <div style="margin-top: 4px;">
                <button type="button" class="add-to-cart-btn" data-order-data='${JSON.stringify(
                  item
                )}' style="background: #1e40af; color: white; border: none; border-radius: 4px; padding: 4px 8px; font-size: 12px; cursor: pointer; font-family: 'Brown Std', sans-serif;">Add to Cart</button>
              </div>
            </div>
            <div class="price" style="color: #1e40af; font-weight: bold; font-family: 'Brown Std', sans-serif;">
              <div class="unit-price" style="font-size: 12px; color: #6b7280;">
                $${(item.calculatedPrice || item.originalPrice || 0).toFixed(2)} each
              </div>
              <div class="total-price" style="font-size: 14px; font-weight: bold;">
                $${(
                  (item.calculatedPrice || item.originalPrice || 0) * (item.quantity || 1)
                ).toFixed(2)}
              </div>
              ${
                item.setupFeeNeeded && item.setupFeePrice && item.setupFeePrice > 0
                  ? `<div style="font-size: 11px; color: #6b7280; margin-top: 2px;">+ $${item.setupFeePrice.toFixed(
                      2
                    )} setup fee (one-time)</div>`
                  : ""
              }
            </div>
          </div>
        `;
          })
          .join("")}
      </div>
    `;
  }

  extractThicknessFromTitle(title) {
    // Extract thickness from title like "1/4 Inch x 6\" x 16\" Custom Cut Plywood"
    const match = title.match(/(\d+\/\d+)\s+Inch/);
    return match ? match[1] : null;
  }

  /**
   * Update the total price display when quantity changes
   * Calculates unit price × quantity and updates the display dynamically
   * @param {HTMLElement} orderItem - The order item element
   * @param {number} quantity - The new quantity
   */
  updateTotalPrice(orderItem, quantity) {
    const addToCartBtn = orderItem.querySelector(".add-to-cart-btn");
    const orderData = JSON.parse(addToCartBtn.dataset.orderData);

    // Get the unit price from the order data
    const unitPrice = orderData.calculatedPrice || orderData.originalPrice || 0;
    const totalPrice = unitPrice * quantity;

    // Update the total price display
    const totalPriceElement = orderItem.querySelector(".total-price");
    if (totalPriceElement) {
      totalPriceElement.textContent = `$${totalPrice.toFixed(2)}`;
    }
  }

  addQuantityEventListeners(popup) {
    // Add event listeners for quantity increase/decrease buttons
    popup.querySelectorAll(".quantity-increase").forEach(button => {
      button.addEventListener("click", e => {
        const quantityDisplay = e.target.parentElement.querySelector(".quantity-display");
        const currentQuantity = parseInt(quantityDisplay.textContent);
        const newQuantity = currentQuantity + 1;
        quantityDisplay.textContent = newQuantity;

        // Update the total price display
        this.updateTotalPrice(e.target.closest(".order-item"), newQuantity);
      });
    });

    popup.querySelectorAll(".quantity-decrease").forEach(button => {
      button.addEventListener("click", e => {
        const quantityDisplay = e.target.parentElement.querySelector(".quantity-display");
        const currentQuantity = parseInt(quantityDisplay.textContent);
        if (currentQuantity > 1) {
          const newQuantity = currentQuantity - 1;
          quantityDisplay.textContent = newQuantity;

          // Update the total price display
          this.updateTotalPrice(e.target.closest(".order-item"), newQuantity);
        }
      });
    });

    // Add event listeners for "Add to Cart" buttons
    popup.querySelectorAll(".add-to-cart-btn").forEach(button => {
      button.addEventListener("click", async e => {
        const orderItem = e.target.closest(".order-item");
        const quantityDisplay = orderItem.querySelector(".quantity-display");
        const quantity = parseInt(quantityDisplay.textContent);

        // Get the order data from the button's data attributes
        const orderData = JSON.parse(e.target.dataset.orderData);

        // Show loading state
        e.target.textContent = "Adding...";
        e.target.disabled = true;

        try {
          // First, remove only custom plywood items WITHOUT dimensions
          await this.removeCustomPlywoodWithoutDimensions();

          // Then add the new item to cart
          await this.addOrderToCart(orderData, quantity);

          // Show success state
          e.target.textContent = "Added!";
          e.target.style.background = "#10b981";

          // Close popup after a short delay
          // setTimeout(() => {
          //   const popup = document.getElementById("custom-plywood-dimensions-popup");
          //   if (popup) {
          //     popup.close();
          //     popup.remove();
          //   }
          // }, 1500);
        } catch (error) {
          console.error("Error adding to cart:", error);
          e.target.textContent = "Error";
          e.target.style.background = "#ef4444";

          // Reset button after 2 seconds
          setTimeout(() => {
            e.target.textContent = "Add to Cart";
            e.target.style.background = "#1e40af";
            e.target.disabled = false;
          }, 2000);
        }
      });
    });

    // Add event listener for "create new order" link
    const createNewOrderLink = popup.querySelector(".create-new-order-link");
    if (createNewOrderLink) {
      createNewOrderLink.addEventListener("click", async e => {
        e.preventDefault(); // Prevent default navigation

        try {
          console.log("Clearing incomplete custom plywood items before creating new order...");

          // Remove incomplete custom plywood items
          await this.removeCustomPlywoodWithoutDimensions();

          // Close popup
          const popup = document.getElementById("custom-plywood-dimensions-popup");
          if (popup) {
            popup.close();
            popup.remove();
          }

          // Navigate to custom plywood product page
          window.location.href = "/products/custom-plywood";
        } catch (error) {
          console.error("Error clearing incomplete items:", error);
          // Still navigate even if clearing fails
          window.location.href = "/products/custom-plywood";
        }
      });
    }

    // Add event listener for "Back to cart" button
    const backToCartBtn = popup.querySelector(".back-to-cart-btn");
    if (backToCartBtn) {
      backToCartBtn.addEventListener("click", async e => {
        try {
          console.log("Clearing incomplete custom plywood items before going back to cart...");

          // Remove incomplete custom plywood items
          await this.removeCustomPlywoodWithoutDimensions();

          // Close popup
          const popup = document.getElementById("custom-plywood-dimensions-popup");
          if (popup) {
            popup.close();
            popup.remove();
          }

          // Navigate to cart page
          window.location.href = "/cart";
        } catch (error) {
          console.error("Error clearing incomplete items and navigating to cart:", error);
          // Still navigate even if clearing fails
          window.location.href = "/cart";
        }
      });
    }
  }

  async removeCustomPlywoodWithoutDimensions() {
    try {
      const cartItems = await this.getCartItems();

      // Find custom plywood items WITHOUT dimensions
      const itemsToRemove = cartItems.filter(item => {
        const isCustomPlywood =
          item.title.includes("Custom Cut Plywood") ||
          item.product_title === "Custom Cut Plywood" ||
          item.properties._setupFeeItemId;

        if (!isCustomPlywood) return false;

        const hasLength =
          item.properties["Length in Inches"] &&
          String(item.properties["Length in Inches"]).trim() !== "";
        const hasWidth =
          item.properties["Width in Inches"] &&
          String(item.properties["Width in Inches"]).trim() !== "";

        return !hasLength || !hasWidth;
      });

      console.log("Items without dimensions to remove:", itemsToRemove);

      // Remove items without dimensions
      for (const item of itemsToRemove) {
        console.log("Removing item without dimensions:", item.title, item.key);
        await this.removeItemFromCart(item.key);
      }

      // Remove ALL setup fees for items without dimensions (both types)
      for (const item of itemsToRemove) {
        if (item.properties._selectionUniqueKey) {
          const setupFeeItems = cartItems.filter(
            cartItem =>
              cartItem.product_type === "Setup Fee" &&
              (cartItem.properties._selectionUniqueKey === item.properties._selectionUniqueKey ||
                cartItem.properties._setupFeeFor === item.properties._selectionUniqueKey)
          );

          for (const setupFee of setupFeeItems) {
            console.log(
              "Removing setup fee for item without dimensions:",
              setupFee.title,
              setupFee.key,
              "Selection key:",
              setupFee.properties._selectionUniqueKey || setupFee.properties._setupFeeFor
            );
            await this.removeItemFromCart(setupFee.key);
          }
        }
      }

      // Dispatch cart change event
      document.dispatchEvent(
        new CustomEvent("on:cart:change", { bubbles: true, cancelable: false })
      );
    } catch (error) {
      console.error("Error removing items without dimensions:", error);
    }
  }

  /**
   * Add order to cart with smart setup fee handling
   * Checks if setup fee is needed based on current cart state before adding
   * @param {Object} orderData - The order data with properties and setup fee info
   * @param {number} quantity - The quantity to add
   */
  async addOrderToCart(orderData, quantity) {
    try {
      // Extract properties from the order data
      const properties = {};
      orderData.properties.forEach(prop => {
        properties[prop.name] = prop.value;
      });

      // Find the variant ID from the selection unique key
      const selectionKey = properties._selectionUniqueKey;
      const variantId = selectionKey.split("-")[0]; // Extract variant ID from "44787441926323-6-16"

      // Extract dimensions from properties
      const lengthValue = properties["Length in Inches"];
      const widthValue = properties["Width in Inches"];

      // Calculate pricing using configurator logic (uses custom_pricing from order data)
      const calculatedPrice = this.calculatePricing(
        orderData.custom_pricing,
        lengthValue,
        widthValue
      );

      // Use configurator logic for property management
      const configuratorProperties = this.buildProperties(
        variantId,
        lengthValue,
        widthValue,
        calculatedPrice,
        properties,
        properties._setupFeeItemId
      );

      // Check if setup fee should be added (using configurator logic)
      const cartItems = await this.getCartItems();
      const shouldAddSetupFee = this.shouldAddSetupFee(cartItems, selectionKey);

      // Prepare items to add to cart
      const itemsToAdd = [
        {
          id: parseInt(variantId),
          quantity: quantity,
          properties: configuratorProperties,
        },
      ];

      // Add setup fee if needed and we have setup fee data
      if (shouldAddSetupFee && orderData.matchingSetupFee && properties._setupFeeItemId) {
        itemsToAdd.push({
          id: parseInt(properties._setupFeeItemId),
          quantity: 1,
          properties: {
            _setupFeeFor: selectionKey,
          },
        });
      }

      // Add items to cart
      const formData = {
        items: itemsToAdd,
      };

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Failed to add to cart: ${response.status}`);
      }

      // Dispatch cart change event - this will trigger the setup fee logic
      document.dispatchEvent(
        new CustomEvent("on:cart:change", { bubbles: true, cancelable: false })
      );

      // Wait a moment for the setup fee to be added
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error("Error adding order to cart:", error);
      throw error;
    }
  }

  async removeItemFromCart(itemKey) {
    try {
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0,
        }),
      });
    } catch (error) {
      console.error("Failed to remove item from cart:", error);
    }
  }
}

// Cookie utility functions
function setCookie(name, value, days = 30) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function deleteCookie(name) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

// Initialize the popup functionality
const customPlywoodPopup = new CustomPlywoodPopup();

// Load saved preferences from cookies and apply them to the popup instance
const savedUseApiData = getCookie("customPlywood_useApiData") === "true";
const savedForcePopup = getCookie("customPlywood_forcePopup") === "true";

// Apply saved preferences to the popup instance
customPlywoodPopup.useApiData = savedUseApiData !== null ? savedUseApiData : true; // Default to true if no cookie
customPlywoodPopup.forcePopup = savedForcePopup !== null ? savedForcePopup : false; // Default to false if no cookie

// Add testing bar to the page
function addTestingBar() {
  // Check if testing bar already exists
  if (document.getElementById("custom-plywood-testing-bar")) {
    return;
  }

  const testingBar = document.createElement("div");
  testingBar.id = "custom-plywood-testing-bar";
  testingBar.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #1e40af;
    color: white;
    padding: 12px;
    z-index: 9999;
    font-family: 'Brown Std', sans-serif;
    font-size: 12px;
    border-bottom: 2px solid #3b82f6;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  `;

  // Load saved preferences from cookies
  const savedUseApiData = getCookie("customPlywood_useApiData") === "true";
  const savedForcePopup = getCookie("customPlywood_forcePopup") === "true";

  testingBar.innerHTML = `
    <div style="display: flex; align-items: center; gap: 16px; flex-wrap: wrap;">
      <div style="font-weight: bold; margin-right: 8px; font-family: 'Brown Std', sans-serif;">🧪 Custom Plywood Testing:</div>
      
      <label style="display: flex; align-items: center; gap: 4px; font-family: 'Brown Std', sans-serif;">
        <input type="checkbox" id="use-api-data" ${savedUseApiData ? "checked" : ""}>
        Use API Data
      </label>
      
      <label style="display: flex; align-items: center; gap: 4px; font-family: 'Brown Std', sans-serif;">
        <input type="checkbox" id="force-popup" ${savedForcePopup ? "checked" : ""}>
        Force Popup
      </label>
      
      <button id="add-incomplete-btn" style="background: #ef4444; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px; font-family: 'Brown Std', sans-serif;">
        Add Incomplete
      </button>
      
      <button id="clear-cart-btn" style="background: #6b7280; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px; font-family: 'Brown Std', sans-serif;">
        Clear Cart
      </button>
      
      <button id="show-popup-btn" style="background: #10b981; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px; font-family: 'Brown Std', sans-serif;">
        Show Popup
      </button>
      
      <button id="clear-settings-btn" style="background: #f59e0b; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px; font-family: 'Brown Std', sans-serif;">
        Clear Settings
      </button>
      
      <button id="toggle-testing-bar-btn" style="background: #8b5cf6; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px; font-family: 'Brown Std', sans-serif;">
        Toggle Bar
      </button>
      
      <button id="hide-testing-bar" style="background: #374151; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 11px; font-family: 'Brown Std', sans-serif;">
        Hide Bar
      </button>
    </div>
  `;

  document.body.appendChild(testingBar);

  // Add event listeners
  document.getElementById("add-incomplete-btn").addEventListener("click", async () => {
    try {
      await window.testAddCustomPlywoodWithoutDimensions();
    } catch (error) {
      console.error("Error adding incomplete product:", error);
    }
  });

  document.getElementById("clear-cart-btn").addEventListener("click", async () => {
    try {
      await window.testClearCart();
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  });

  document.getElementById("show-popup-btn").addEventListener("click", () => {
    try {
      window.customPlywoodPopup.showCustomPlywoodDimensionsPopup();
    } catch (error) {
      console.error("Error showing popup:", error);
    }
  });

  document.getElementById("clear-settings-btn").addEventListener("click", () => {
    // Clear all custom plywood cookies
    deleteCookie("customPlywood_useApiData");
    deleteCookie("customPlywood_forcePopup");

    // Reset checkboxes to defaults
    document.getElementById("use-api-data").checked = true;
    document.getElementById("force-popup").checked = false;

    // Reset popup instance properties
    customPlywoodPopup.useApiData = true;
    customPlywoodPopup.forcePopup = false;

    console.log("Custom plywood settings cleared and reset to defaults");
  });

  document.getElementById("toggle-testing-bar-btn").addEventListener("click", () => {
    // Toggle the cookie override for showing testing bar
    const currentOverride = getCookie("customPlywood_showTestingBar") === "true";
    const newOverride = !currentOverride;

    if (newOverride) {
      setCookie("customPlywood_showTestingBar", "true");
      console.log("Testing bar override enabled - will show on page reload");
    } else {
      deleteCookie("customPlywood_showTestingBar");
      console.log("Testing bar override disabled - will hide on page reload");
    }

    // Show/hide current bar immediately
    testingBar.style.display = newOverride ? "block" : "none";
  });

  document.getElementById("hide-testing-bar").addEventListener("click", () => {
    testingBar.style.display = "none";
  });

  // Handle API data toggle
  document.getElementById("use-api-data").addEventListener("change", e => {
    const useApiData = e.target.checked;
    console.log("API data toggle:", useApiData ? "ON" : "OFF");
    // This will be used by the fetchPreviousCustomPlywoodOrders method
    window.customPlywoodPopup.useApiData = useApiData;
    // Save to cookie
    setCookie("customPlywood_useApiData", useApiData.toString());
  });

  // Handle force popup toggle
  document.getElementById("force-popup").addEventListener("change", e => {
    const forcePopup = e.target.checked;
    console.log("Force popup toggle:", forcePopup ? "ON" : "OFF");
    window.customPlywoodPopup.forcePopup = forcePopup;
    // Save to cookie
    setCookie("customPlywood_forcePopup", forcePopup.toString());
  });
}

// Check if testing bar should be shown based on theme setting
function shouldShowTestingBar() {
  // Check if theme setting is enabled
  const themeSetting =
    window.theme && window.theme.settings && window.theme.settings.customPlywoodTestingBar;

  // Also check for cookie override (for development)
  const cookieOverride = getCookie("customPlywood_showTestingBar") === "true";

  // Show if theme setting is enabled OR cookie override is set
  return themeSetting === true || cookieOverride === true;
}

// Add testing bar when DOM is ready (only if setting allows it)
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    if (shouldShowTestingBar()) {
      addTestingBar();
    }
  });
} else {
  if (shouldShowTestingBar()) {
    addTestingBar();
  }
}

// Testing function - expose to global scope for console testing
window.testAddCustomPlywoodWithoutDimensions = async function () {
  try {
    console.log("Adding custom plywood without dimensions for testing...");

    // Add a custom plywood item without dimensions
    const formData = {
      items: [
        {
          id: 44787441926323, // Custom Cut Plywood variant ID
          quantity: 1,
        },
      ],
    };

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error(`Failed to add to cart: ${response.status}`);
    }

    const result = await response.json();
    console.log("Successfully added custom plywood without dimensions:", result);

    // Trigger cart change event to show popup
    document.dispatchEvent(new CustomEvent("on:cart:change", { bubbles: true, cancelable: false }));

    console.log(
      "Cart change event dispatched - popup should appear if custom plywood detection works"
    );
  } catch (error) {
    console.error("Error adding custom plywood without dimensions:", error);
  }
};

// Testing function to clear cart for testing
window.testClearCart = async function () {
  try {
    console.log("Clearing cart for testing...");

    const cartItems = await customPlywoodPopup.getCartItems();

    for (const item of cartItems) {
      console.log("Removing item:", item.title, item.key);
      await customPlywoodPopup.removeItemFromCart(item.key);
    }

    console.log("Cart cleared successfully");

    // Trigger cart change event
    document.dispatchEvent(new CustomEvent("on:cart:change", { bubbles: true, cancelable: false }));
  } catch (error) {
    console.error("Error clearing cart:", error);
  }
};

// Also expose the popup instance for advanced testing
window.customPlywoodPopup = customPlywoodPopup;

// Global function to show testing bar programmatically
window.showCustomPlywoodTestingBar = function () {
  setCookie("customPlywood_showTestingBar", "true");
  if (document.getElementById("custom-plywood-testing-bar")) {
    document.getElementById("custom-plywood-testing-bar").style.display = "block";
  } else {
    addTestingBar();
  }
  console.log("Custom plywood testing bar enabled");
};

// Global function to hide testing bar programmatically
window.hideCustomPlywoodTestingBar = function () {
  deleteCookie("customPlywood_showTestingBar");
  const testingBar = document.getElementById("custom-plywood-testing-bar");
  if (testingBar) {
    testingBar.style.display = "none";
  }
  console.log("Custom plywood testing bar disabled");
};
